import React from 'react';
import {
  House,
  Building2,
  Factory,
  Landmark,
} from "lucide-react";

import {
  TargetIcon,
  EyeIcon,
} from '../components/common/Icons';
import './About.css';

const About = () => {
  return (
    <div className="about-page">

      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>About Us</h1>
          <p>
            Learn more about Neha Shuttering and our commitment to the construction industry.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
            <section className="section bg-white">
              <div className="container">

                <div className="mission-vision">

                  {/* Left Column */}
                  <div className="mission-column">

                    <div className="mv-icon">
                      <TargetIcon />
                    </div>

                    <h3>Our Mission</h3>

                    <p>
                      To empower the construction industry with safe, reliable and
                      innovative shuttering and scaffolding solutions that improve
                      productivity while maintaining the highest standards of quality,
                      durability and worker safety.
                    </p>

                  </div>

                  {/* Center Divider */}
                  <div className="mv-divider"></div>

                  {/* Right Column */}
                  <div className="vision-column">
                    <div className="mv-icon">
                      <EyeIcon />
                    </div>

                    <h3>Our Vision</h3>
                    <p>
                      To become one of India's most trusted names in shuttering and
                      scaffolding by consistently delivering dependable products,
                      exceptional service and long-term value for builders,
                      contractors and infrastructure projects.
                    </p>
                  </div>
                </div>
              </div>
            </section>

      {/* Company Overview */}
      <section
        className="section"
        style={{
          position: "relative",
          backgroundImage: "url('/companyoverview.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden"
        }}
      >
        {/* Overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.65)"
          }}
        />

        <div
          className="container"
          style={{
            position: "relative",
            zIndex: 2,
            minHeight: "600px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            color: "#fff",
            padding: "40px 0"
          }}
        >
          <h2
            style={{
              color: "#fff",
              marginBottom: "35px"
            }}
          >
            About Neha Shuttering Store
          </h2>

          <div style={{ maxWidth: "1000px" }}>
            <p
              style={{
                lineHeight: "1.9",
                fontSize: "1.1rem",
                marginBottom: "20px"
              }}
            >
              Neha Shuttering Store is a trusted name in the shuttering and scaffolding industry, proudly serving the construction sector since 1985. The company was founded by Late Shri Bhim Singh Saini, whose vision, integrity, and commitment to quality laid the foundation for a business built on trust and long-term customer relationships.
            </p>

            <p
              style={{
                lineHeight: "1.9",
                fontSize: "1.1rem",
                marginBottom: "20px"
              }}
            >
              With 40+ years of industry experience, we have successfully supported residential, commercial, industrial, and infrastructure projects by providing reliable shuttering and scaffolding solutions. We are also working with many reputed builders in the industry. Our focus has always been on delivering high-quality materials, timely service, and dependable support that help our clients complete their projects efficiently and safely.
            </p>

            <p
              style={{
                lineHeight: "1.9",
                fontSize: "1.1rem",
                marginBottom: "20px"
              }}
            >
              Since 2012, the business has been led by Mr. Keshav Saini, son of Late Shri Bhim Singh Saini, who continues to carry forward his father's legacy with a modern approach, customer-first philosophy, and unwavering commitment to excellence. Under his leadership, Neha Shuttering Store has strengthened its reputation for quality, reliability, and professional service.
            </p>

            <p
              style={{
                lineHeight: "1.9",
                fontSize: "1.1rem",
                marginBottom: "20px"
              }}
            >
              At Neha Shuttering Store, we believe that every successful construction project begins with a strong foundation. Our mission is to provide durable, high-quality shuttering and scaffolding materials backed by exceptional customer service, ensuring that our clients can build with confidence.
            </p>

            <h3 style={{ color: "var(--primary-color)", marginTop: "30px", fontSize: "1.5rem" }}>
              Our Legacy. Your Foundation.
            </h3>
          </div>
        </div>
      </section>

      {/* Product Range Description */}
      <section className="section">
        <div className="container">

          <h2 className="text-center">
            Comprehensive Product Range
          </h2>

          <p className="text-center max-800">
            We offer a wide array of products tailored to meet the specific
            needs of modern construction. From basic shuttering plates to
            advanced cuplock systems, our inventory is designed to support every
            stage of your build.
          </p>

          <div className="grid grid-3 mt-40">

            <div className="range-item">
              <h4>Shuttering Systems</h4>
              <p>
                Durable plates and channels for precise concrete forming.
              </p>
            </div>

            <div className="range-item">
              <h4>Support Equipment</h4>
              <p>
                Heavy-duty props, spans and jacks for reliable structural
                support.
              </p>
            </div>

            <div className="range-item">
              <h4>Access Solutions</h4>
              <p>
                Versatile scaffolding and cuplock systems for safe site access.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section bg-dark text-white">

        <div className="container">

          <h2 className="text-center text-white">
            Industries We Serve
          </h2>

          <div className="grid grid-4 text-center mt-40">

            <div className="industry-item">
              <div className="industry-icon">
                <House size={52} strokeWidth={1.8} />
              </div>
              <p>Residential Buildings</p>
            </div>

            <div className="industry-item">
              <div className="industry-icon">
                <Building2 size={52} strokeWidth={1.8} />
              </div>
              <p>Commercial Complexes</p>
            </div>

            <div className="industry-item">
              <div className="industry-icon">
                <Factory size={52} strokeWidth={1.8} />
              </div>
              <p>Industrial Projects</p>
            </div>

            <div className="industry-item">
              <div className="industry-icon">
                <Landmark size={52} strokeWidth={1.8} />
              </div>
              <p>Infrastructure & Bridges</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects in Action */}
      <section className="section bg-light">
        <div className="container">
          <h2 className="text-center">Projects in Action</h2>
          <p className="text-center max-800 mb-40">
            Our high-grade MS shuttering and support systems in action across various large-scale construction sites, providing the strength and precision needed for critical structural works.
          </p>
          <div className="grid grid-2 gallery">
            <div className="facility-card">
              <img src="/img1.jpg" alt="Column Shuttering" />
              <div className="facility-info">
                <h4>Precision Column Casting</h4>
                <p>Specialized MS Column Farma plates and vertical shuttering systems being utilized for casting heavy-duty structural columns in a major infrastructure project.</p>
              </div>
            </div>
            <div className="facility-card">
              <img src="/img2.jpg" alt="Foundation Support" />
              <div className="facility-info">
                <h4>Foundation & Reinforcement Support</h4>
                <p>Large-scale deployment of our shuttering plates and structural supports for massive foundation works, ensuring stability for high-rise reinforcement structures.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;